import { useMemo } from "react";
import type { ErrorLike } from "@apollo/client";
import { skipToken, useQuery } from "@apollo/client/react";
import { IssueListGroupCountsDocument, type IssueMeasureInput } from "#app/lib/data";
import type { MeasureFn } from "@salestrak/widgets";
import type { Omittable } from "@salestrak/ui";

type UseIssueGroupCountsOptions = {
  where: Record<string, unknown> | undefined;
  groupBy: string | undefined;
  /** Aggregate number/money custom fields per bucket beside the count — one
   *  entry per field, answered index-aligned in `values`. Empty/absent = counts
   *  only. Pass a memoized array: it is a query variable. */
  measures?: readonly { fieldId: string; fn: MeasureFn }[] | null | undefined;
};

type UseIssueGroupCountsResult = Omittable<{
  /**
   * The answer in hand. The current variables' once `ready`; until then the
   * PREVIOUS variables' — Apollo drops `data` the moment the variables change,
   * and a consumer that keeps its shape through a re-query (a band that
   * skeletons only its figures, a drill whose rows the figure named) needs the
   * last answer to keep it from. Empty before the first answer and while the
   * query is skipped.
   */
  counts: Record<string, number>;
  /** Per key, the measured aggregates in `measures` order — present only when
   *  measures were asked; `null` entries mark buckets none of whose projects
   *  hold that field. */
  values: Record<string, Array<number | null>>;
  /** The current variables have answered. */
  ready: boolean;
  /** The query error, if any — a widget shows it as a `WidgetError` state. */
  error?: ErrorLike;
}>;

// The viewer's IANA zone, resolved once — time-series groupBys bucket in this
// calendar server-side (S7) so boundary activity lands in the viewer's period,
// not UTC's. Categorical groupBys ignore it. Module-level: the zone cannot
// change without a reload, and a stable value keeps query variables stable.
const VIEWER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

export function useIssueGroupCounts({
  where,
  groupBy,
  measures,
}: UseIssueGroupCountsOptions): UseIssueGroupCountsResult {
  const measureInputs = useMemo<IssueMeasureInput[] | null>(
    // The grammar's fn names ARE the schema enum's values (`IssueMeasureFn`
    // registers `MEASURE_FNS`), so each token passes through untranslated.
    () =>
      measures && measures.length > 0
        ? measures.map((measure) => ({ fieldId: measure.fieldId, fn: measure.fn }))
        : null,
    [measures],
  );
  const variables = useMemo(
    () =>
      where && groupBy
        ? { where, groupBy, timezone: VIEWER_TIMEZONE, measures: measureInputs }
        : undefined,
    [where, groupBy, measureInputs],
  );

  const { data, previousData, error } = useQuery(
    IssueListGroupCountsDocument,
    variables ? { variables, fetchPolicy: "cache-and-network" } : skipToken,
  );

  // A skipped query holds nothing: its previous answer was to a question no
  // longer asked. `previousData` is typed as a deep partial; it is a whole
  // result this hook received, so the cast restores the shape `data` has.
  const settled = data ?? (variables ? (previousData as typeof data) : undefined);
  const { counts, values } = useMemo(() => {
    const counts: Record<string, number> = {};
    const values: Record<string, Array<number | null>> = {};
    if (settled?.issueGroupCounts) {
      for (const { key, total, values: figures } of settled.issueGroupCounts) {
        counts[key] = total;
        if (figures) values[key] = figures.map((figure) => figure ?? null);
      }
    }
    return { counts, values };
  }, [settled]);

  return { counts, values, ready: data?.issueGroupCounts !== undefined, error };
}
